import type { SupabaseClient } from '@supabase/supabase-js'
import type { z } from 'zod/v4'
import { isEqual } from 'lodash-es'
import type { Database, Enums } from '../db/supabase.types.ts'
import { assetRelationDataSchema } from './assetRelationTypes.ts'
import { parseAssetRelation } from './utils.ts'

type RelationSelector =
  | {
      assetId: string
      userId: string
      relationType: Enums<'user_asset_relation'>
    }
  | {
      relationId: string
    }

export async function updateAssetRelation(
  {
    data,
    setRelationType,
    ...selector
  }: {
    setRelationType?: Enums<'user_asset_relation'>
    data?: z.infer<typeof assetRelationDataSchema>
  } & RelationSelector,
  ctx: { supabase: SupabaseClient<Database> }
) {
  const validatedData = data ? assetRelationDataSchema.parse(data) : undefined

  const query = ctx.supabase.from('users_assets').select('*')

  if ('assetId' in selector) {
    query.eq('asset_id', selector.assetId)
    query.eq('user_id', selector.userId)
    query.eq('relation_type', selector.relationType)
  } else {
    query.eq('id', selector.relationId)
  }

  const { data: relation, error: relationError } = await query.maybeSingle()

  if (relationError || !relation) {
    if (relationError) {
      console.error('Relation not found', selector, relationError)
    }

    return null
  }

  if (
    (!setRelationType || relation.relation_type === setRelationType) &&
    isEqual(relation.data, validatedData)
  ) {
    return parseAssetRelation(relation)
  }

  const nextData = assetRelationDataSchema.parse({
    ...assetRelationDataSchema.safeParse(relation.data)?.data,
    ...validatedData,
  })

  const updateQuery = ctx.supabase.from('users_assets').update({
    relation_type: setRelationType || relation.relation_type,
    data: nextData,
  })

  if ('assetId' in selector) {
    updateQuery.eq('asset_id', selector.assetId)
    updateQuery.eq('user_id', selector.userId)
    updateQuery.eq('relation_type', selector.relationType)
  } else {
    updateQuery.eq('id', selector.relationId)
  }

  const { data: updatedRelation, error } = await updateQuery.select().single()

  if (error || !updatedRelation) {
    console.error(error)
    return parseAssetRelation(relation)
  }

  return parseAssetRelation(updatedRelation)
}
